import React, { useState } from 'react';
import { 
  User, Building2, Mail, Phone, MapPin, CreditCard,
  Save, Edit3, X, Check, Clock, Percent, AlertCircle,
  Shield, FileText, Hash, Calendar
} from 'lucide-react';
import { clientConnecte, entreprise, formatMontant } from '../data/mockData';

const Profil = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    nom: clientConnecte.nom,
    prenom: clientConnecte.prenom,
    email: clientConnecte.email,
    telephone: clientConnecte.telephone,
    portable: clientConnecte.portable,
    adresse: clientConnecte.adresse,
    codePostal: clientConnecte.codePostal,
    ville: clientConnecte.ville,
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      nom: clientConnecte.nom,
      prenom: clientConnecte.prenom,
      email: clientConnecte.email,
      telephone: clientConnecte.telephone,
      portable: clientConnecte.portable,
      adresse: clientConnecte.adresse,
      codePostal: clientConnecte.codePostal,
      ville: clientConnecte.ville,
    });
    setIsEditing(false);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Mon profil</h1>
          <p className="page-subtitle">Gérez vos informations et consultez vos conditions commerciales</p>
        </div>
      </div>

      <div className="profil-grid">
        {/* Carte identité */}
        <div className="profil-identity-card">
          <div className="profil-avatar">
            <span>{clientConnecte.prenom[0]}{clientConnecte.nom[0]}</span>
          </div>
          <h2>{clientConnecte.prenom} {clientConnecte.nom}</h2>
          <p className="profil-company">{clientConnecte.raisonSociale}</p>
          <div className="profil-meta">
            <span><Hash size={14} /> {clientConnecte.id}</span>
            <span><Calendar size={14} /> Client depuis {new Date(clientConnecte.dateCreation).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</span>
          </div>
          <div className="profil-commercial">
            <User size={16} />
            <div>
              <span className="profil-commercial-label">Votre commercial</span>
              <span className="profil-commercial-name">{clientConnecte.commercial}</span>
            </div>
          </div>
        </div>

        <div className="profil-main">
          {/* Coordonnées */}
          <div className="profil-card">
            <div className="profil-card-header">
              <h2><Building2 size={20} /> Coordonnées</h2>
              {!isEditing ? (
                <button className="btn-secondary" onClick={() => setIsEditing(true)}>
                  <Edit3 size={16} /> Modifier
                </button>
              ) : (
                <div className="profil-edit-actions">
                  <button className="btn-secondary" onClick={handleCancel}>
                    <X size={16} /> Annuler
                  </button>
                  <button className="btn-primary" onClick={handleSave}>
                    <Save size={16} /> Enregistrer
                  </button>
                </div>
              )}
            </div>

            <div className="profil-form-grid">
              <div className="profil-field">
                <label><User size={14} /> Nom</label>
                {isEditing ? (
                  <input type="text" value={formData.nom} onChange={(e) => handleChange('nom', e.target.value)} />
                ) : (
                  <p>{formData.nom}</p>
                )}
              </div>
              <div className="profil-field">
                <label><User size={14} /> Prénom</label>
                {isEditing ? (
                  <input type="text" value={formData.prenom} onChange={(e) => handleChange('prenom', e.target.value)} />
                ) : (
                  <p>{formData.prenom}</p>
                )}
              </div>
              <div className="profil-field">
                <label><Mail size={14} /> Email</label>
                {isEditing ? (
                  <input type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} />
                ) : (
                  <p>{formData.email}</p>
                )}
              </div>
              <div className="profil-field">
                <label><Phone size={14} /> Téléphone</label>
                {isEditing ? (
                  <input type="tel" value={formData.telephone} onChange={(e) => handleChange('telephone', e.target.value)} />
                ) : (
                  <p>{formData.telephone}</p>
                )}
              </div>
              <div className="profil-field">
                <label><Phone size={14} /> Portable</label>
                {isEditing ? (
                  <input type="tel" value={formData.portable} onChange={(e) => handleChange('portable', e.target.value)} />
                ) : (
                  <p>{formData.portable}</p>
                )}
              </div>
              <div className="profil-field profil-field-full">
                <label><MapPin size={14} /> Adresse</label>
                {isEditing ? (
                  <div className="profil-address-fields">
                    <input type="text" value={formData.adresse} onChange={(e) => handleChange('adresse', e.target.value)} placeholder="Adresse" />
                    <div className="profil-address-row">
                      <input type="text" value={formData.codePostal} onChange={(e) => handleChange('codePostal', e.target.value)} placeholder="Code postal" style={{ maxWidth: '120px' }} />
                      <input type="text" value={formData.ville} onChange={(e) => handleChange('ville', e.target.value)} placeholder="Ville" />
                    </div>
                  </div>
                ) : (
                  <p>{formData.adresse}, {formData.codePostal} {formData.ville}</p>
                )}
              </div>
            </div>

            {/* Infos société (lecture seule) */}
            <div className="profil-readonly-section">
              <h3><Shield size={16} /> Informations société (non modifiables)</h3>
              <div className="profil-form-grid">
                <div className="profil-field">
                  <label>Raison sociale</label>
                  <p>{clientConnecte.raisonSociale}</p>
                </div>
                <div className="profil-field">
                  <label>SIRET</label>
                  <p className="mono-text">{clientConnecte.siret}</p>
                </div>
                <div className="profil-field">
                  <label>TVA intracommunautaire</label>
                  <p className="mono-text">{clientConnecte.tvaIntra}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Conditions de paiement */}
          <div className="profil-card">
            <div className="profil-card-header">
              <h2><CreditCard size={20} /> Conditions de paiement</h2>
            </div>
            <div className="conditions-grid">
              <div className="condition-item">
                <div className="condition-icon condition-icon-blue">
                  <Clock size={20} />
                </div>
                <div>
                  <span className="condition-label">Délai de paiement</span>
                  <span className="condition-value">{clientConnecte.conditionsPaiement.delai} jours</span>
                </div>
              </div>
              <div className="condition-item">
                <div className="condition-icon condition-icon-green">
                  <CreditCard size={20} />
                </div>
                <div>
                  <span className="condition-label">Mode de paiement</span>
                  <span className="condition-value">{clientConnecte.conditionsPaiement.mode}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profil;
